import * as React from 'react'

import { Box, Hint } from '@factorialco/deprecated-design-system'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import { useLocalStorage } from 'usehooks-ts'

import { observer } from 'mobx-react'

import useEmployeeMultiFilters, {
  applyFilters,
} from 'modules/core/components/EmployeeMultiFilters/hooks/useEmployeeMultiFilters'
import employeeStatuses from 'modules/core/components/EmployeeMultiFilters/hooks/utils/employeeStatuses'
import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import presences from 'modules/attendance/store/collections/presences'
import storedEmployees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import locations from 'modules/locations/store/collections/locations'
import {
  SHIFT_CONFLICTS,
  SHIFT_MANAGEMENT_FILTERS,
} from 'modules/shift_management/hooks/reducers/filter'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useFilters } from 'modules/shift_management/hooks/useFilters'
import { usePagination } from 'modules/shift_management/hooks/usePagination'
import { usePermissions } from 'modules/shift_management/hooks/usePermissions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useSelectableEmployees } from 'modules/shift_management/hooks/useSelectableEmployees'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { usePlanningVersions } from 'modules/time_planning/store/collections/planningVersions'

import Body from './Body'
import EmptyBody from './EmptyBody'
import FlashNoticeState from './FlashNoticeState'
import TableHeader from './TableHeader'
import {
  formatCustomFilters,
  getCustomFilters,
  isPastEmployees,
  isStatusConflict,
  isStatusWithoutConflict,
} from './_filters/customFilters'
import getFilterValue from './_filters/getFilterValue'
import * as styles from './index.vanilla.css'
import useFetchPlanningBalances from './useFetchPlanningBalances'

const Content = () => {
  const [{ shiftsRows }, dispatchShiftsRows] = useShiftsRows()
  const [{ view, shiftsConflicts }] = useSchedule()
  const [, dispatchPagination] = usePagination()
  const [{ locationIds }, dispatchFilters] = useFilters()
  const [{ isLoading }, dispatchUI] = useUI()
  const [{ canManageShifts }] = usePermissions()
  const { selectableEmployeeIds, loading: loadingSelectableEmployees } = useSelectableEmployees(
    view.startAt,
    view.endAt,
    'read'
  )

  const [showCoverageSection, setShowCoverageSection] = React.useState<boolean>(false)
  const filtersAppliedFromStorageRef = React.useRef<boolean>(false)
  const previousShiftIdsRef = React.useRef<string>('')
  const planningVersions = usePlanningVersions()
  const hasNoneShiftManagementPlanning = !planningVersions.shiftManagementTool.length
  const isAnyTimezoneEmpty = locations.some((loc) => loc.isTimezoneEmpty)
  const { employee, allMyDescendants } = useCurrentSession()

  const { balancesByEmployee, periodBalancesByEmployee } = useFetchPlanningBalances()

  // WIP: Will be removed in the next PR for release of feature
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const [hasSeenNewEntryFlowHints, setHasSeenNewEntryFlowHints] = useLocalStorage<boolean>(
    LOCAL_STORAGE_KEYS.shiftManagementHasSeenNewEntryFlowHints,
    false
  )

  const {
    logicOperator,
    filters,
    onApplyFilters,
    onCancel,
    onRemoveFilter,
    onAddFilter,
    resetFilter,
  } = useEmployeeMultiFilters<Employee>(getFilterValue, LOCAL_STORAGE_KEYS.shiftEmployees, {
    newEntryFlowEnabled,
  })

  useUnsafeEffect(() => {
    if (locationIds.length <= 0 || newEntryFlowEnabled) return

    const newFilters = locationIds.map((locationId) => {
      const existingFilter = filters.find(
        (f) => f.values.field === SHIFT_MANAGEMENT_FILTERS.employeeLocation
      )
      const condition = existingFilter?.values.condition || CONDITIONS_VALUES.isEqual

      return new MultiFilterForm({
        condition: condition,
        field: SHIFT_MANAGEMENT_FILTERS.employeeLocation,
        value: locationId,
      })
    })

    const resetFilters = [
      ...filters.filter((fi) => fi.values.field !== SHIFT_MANAGEMENT_FILTERS.employeeLocation),
      ...newFilters,
    ]
    resetFilter(resetFilters)
  }, [locationIds, newEntryFlowEnabled])

  useUnsafeEffect(() => {
    if (locationIds.length <= 0) return

    if (!newEntryFlowEnabled) {
      updateFilters()
      dispatchFilters({
        type: 'SET_LOCATIONS',
        locationIds: [],
      })
    }
  }, [filters, newEntryFlowEnabled])

  const getFilteredEmployees = async () => {
    await presences.fetch({ withCachedFetchParams: true })
    const value = await applyFilters(
      storedEmployees.toArray(),
      filters,
      logicOperator,
      getFilterValue,
      () => undefined,
      employee,
      allMyDescendants
    )

    // If filtering for terminated employees, return without selectableEmployeeIds filter
    if (isPastEmployees(filters)) {
      return value
    }

    // FCT-38778: Filter by READ permissions for display
    // useSelectableEmployees is called with action='read' above, which returns all
    // employees that the user can see in shift management (not just those they can create shifts for)
    return value.filter((e) => selectableEmployeeIds.includes(e.id))
  }

  const updateCustomFilters = async () => {
    const filteredEmployees = await getFilteredEmployees()
    const hasNonCustomFilters = filters.some(
      (filter) => !filter.get('custom_action').value && !!filter.get('condition').value
    )
    const isOrOperator = logicOperator.get('logic_operator').value === 'or'

    const customAction = filters.find((filter) => filter.get('custom_action').value)
    const terminated = isPastEmployees(filters)
    const filterByConflict = isStatusConflict(filters)
    const filterByWithoutConflict = isStatusWithoutConflict(filters)
    const filterRequiresEmployeeIds =
      hasNonCustomFilters || filterByConflict || filterByWithoutConflict

    const employeeIds =
      filterRequiresEmployeeIds || !isOrOperator
        ? filteredEmployees.map((e) => e.id.toString())
        : []

    if (customAction || terminated) {
      dispatchFilters({
        type: 'FILTER_CUSTOM',
        id: filterByConflict ? SHIFT_CONFLICTS : '',
        employeeIds: employeeIds,
        customFilters: formatCustomFilters(getCustomFilters(filters)),
        logicOperator: logicOperator,
        shiftIds: filterByConflict ? shiftsConflicts.shiftIds : null,
        withoutShiftsIds: filterByWithoutConflict ? shiftsConflicts.shiftIds : null,
        terminated: terminated,
      })
    } else if (filters.length > 0) {
      dispatchFilters({
        type: 'FILTER_EMPLOYEE_IDS',
        employeeIds: employeeIds,
      })
    } else {
      dispatchFilters({ type: 'CLEAR_FILTERS', newEntryFlowEnabled: newEntryFlowEnabled })
    }
    dispatchUI({ type: 'LOADING' })
    dispatchShiftsRows({ type: 'RESET_SHIFTS_ROWS' })
    dispatchPagination({ type: 'RESET_PAGINATION' })
  }

  const addFilter = () => {
    onAddFilter()
    track(trackingEvents.shiftManagement.filterShiftManagementAddNewFilter, {
      view: view.viewType,
      view_type_by: view.groupType,
    })
  }

  const updateFilters = () => {
    if (!newEntryFlowEnabled) {
      dispatchFilters({
        type: 'SET_LOCATIONS',
        locationIds: filters
          .filter((f) => f.get('field').value === SHIFT_MANAGEMENT_FILTERS.employeeLocation)
          .map((f) => f.get('value').value),
      })
    }
    onApplyFilters(storedEmployees.toArray())

    const filtersFullInformation = filters.map((filter) => {
      const field = filter.get('field').value
      const condition = filter.get('condition').value
      const value = filter.get('value').value

      return [field, condition, value].join(' ')
    })

    let locationFilterCount = 0
    filtersFullInformation.some((f) => {
      if (f.split(' ').at(0) === 'location') locationFilterCount++
      return locationFilterCount > 1
    })
    if (locationFilterCount > 1 || locationFilterCount === 0) setShowCoverageSection(false)

    const isActiveEmployeesFilterApplied = filtersFullInformation
      .at(0)
      ?.includes(employeeStatuses.active)

    // Prevent event triggering when exployee_status is_equal employee_active filter is applied
    if (!isActiveEmployeesFilterApplied) {
      track(trackingEvents.shiftManagement.filterShiftManagementSubmit, {
        view: view.viewType,
        view_type_by: view.groupType,
        where: filters.map((filter) => filter.get('field').value),
        number_of_filters: filters.length,
        join_operator: filters.length <= 1 ? null : logicOperator.values.logic_operator,
        full_filters: !filtersFullInformation.length ? null : filtersFullInformation,
      })
    }

    updateCustomFilters()
  }

  const onResetFilters = () => {
    track(trackingEvents.shiftManagement.filterShiftManagementReset, {
      view: view.viewType,
      view_type_by: view.groupType,
    })
    updateCustomFilters()
  }
  useEffect(() => {
    onApplyFilters(storedEmployees.toArray())
  }, [shiftsRows.length]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (
      !filtersAppliedFromStorageRef.current &&
      filters.length > 0 &&
      !loadingSelectableEmployees &&
      selectableEmployeeIds.length > 0
    ) {
      filtersAppliedFromStorageRef.current = true
      previousShiftIdsRef.current = JSON.stringify([...shiftsConflicts.shiftIds].sort())
      updateCustomFilters()
    }
  }, [filters.length, loadingSelectableEmployees, selectableEmployeeIds.length]) // eslint-disable-line react-hooks/exhaustive-deps

  useUnsafeEffect(() => {
    if (!filtersAppliedFromStorageRef.current) {
      return
    }

    const currentShiftIds = JSON.stringify([...shiftsConflicts.shiftIds].sort())
    const shiftIdsChanged = previousShiftIdsRef.current !== currentShiftIds

    // Reapply filters if:
    // 1. shiftIds changed (shiftsConflicts was updated for new view/period)
    // 2. There are filters to reapply
    // 3. Data is ready (not loading, selectableEmployees available)
    // Note: We reapply all filters, but especially important for custom action filters (like status)
    // that depend on shiftIds from conflicts
    if (
      shiftIdsChanged &&
      filters.length > 0 &&
      !loadingSelectableEmployees &&
      selectableEmployeeIds.length > 0
    ) {
      previousShiftIdsRef.current = currentShiftIds
      updateCustomFilters()
    } else if (shiftIdsChanged) {
      previousShiftIdsRef.current = currentShiftIds
    }
  }, [filters.length, loadingSelectableEmployees, selectableEmployeeIds.length])

  return (
    <>
      {canManageShifts && isAnyTimezoneEmpty && (
        <Box className={styles.sticky}>
          <FlashNoticeState />
        </Box>
      )}

      {!hasSeenNewEntryFlowHints && newEntryFlowEnabled ? (
        <Hint.Popover.Provider
          steps={3}
          onClose={() => setHasSeenNewEntryFlowHints(true)}
          onFinish={() => setHasSeenNewEntryFlowHints(true)}
        >
          <>
            <TableHeader
              onAddFilter={() => addFilter()}
              onRemoveFilter={onRemoveFilter}
              onApplyFilters={() => updateFilters()}
              onCancel={onCancel}
              onResetFilters={onResetFilters}
              filters={filters}
              logicOperator={logicOperator}
              hasNoneShiftManagementPlanning={hasNoneShiftManagementPlanning}
              setShowCoverageSection={setShowCoverageSection}
              showCoverageSection={showCoverageSection}
            />
            {hasNoneShiftManagementPlanning ? (
              <EmptyBody isLoading={isLoading} />
            ) : (
              <Body
                shiftsRows={shiftsRows}
                balancesByEmployee={balancesByEmployee}
                periodBalancesByEmployee={periodBalancesByEmployee}
                isLoading={isLoading}
                view={view}
                showCoverageSection={showCoverageSection}
              />
            )}
          </>
        </Hint.Popover.Provider>
      ) : (
        <>
          <TableHeader
            onAddFilter={() => addFilter()}
            onRemoveFilter={onRemoveFilter}
            onApplyFilters={() => updateFilters()}
            onCancel={onCancel}
            onResetFilters={onResetFilters}
            filters={filters}
            logicOperator={logicOperator}
            hasNoneShiftManagementPlanning={hasNoneShiftManagementPlanning}
            setShowCoverageSection={setShowCoverageSection}
            showCoverageSection={showCoverageSection}
          />
          {hasNoneShiftManagementPlanning ? (
            <EmptyBody isLoading={isLoading} />
          ) : (
            <Body
              shiftsRows={shiftsRows}
              balancesByEmployee={balancesByEmployee}
              periodBalancesByEmployee={periodBalancesByEmployee}
              isLoading={isLoading}
              view={view}
              showCoverageSection={showCoverageSection}
            />
          )}
        </>
      )}
    </>
  )
}

export default observer(Content)
